import { useMutation, useQueryClient, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeDeleteResponseDto } from '@personnel/application/dtos';
import { useApiErrorHandler } from './useApiErrorHandler';
import { ROLES_QUERY_KEYS } from './useRoleList';
import { EMPLOYEES_QUERY_KEYS } from './useDeleteEmployee';

/**
 * Hook options for configuring delete behavior
 */
export interface UsePersonnelDeleteEmployeeOptions {
  onSuccess?: (uuid: string) => void;
  onError?: (error: Error, uuid: string) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

/**
 * Hook result with mutation function and states
 */
export interface UsePersonnelDeleteEmployeeResult {
  deleteEmployee: (uuid: string) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for deleting an employee within Personnel domain
 *
 * Uses composition layer for dependency injection.
 * Automatically invalidates the employees list cache on success.
 *
 * @param {UsePersonnelDeleteEmployeeOptions} options - Hook configuration options
 * @returns {UsePersonnelDeleteEmployeeResult} Mutation result with typed interface
 *
 * @example
 * const { deleteEmployee, isLoading } = usePersonnelDeleteEmployee({
 *   onSuccess: (uuid) => console.log('Deleted:', uuid),
 *   showSuccessToast: true
 * });
 *
 * const handleDelete = async (uuid: string) => {
 *   await deleteEmployee(uuid);
 * };
 */
export const usePersonnelDeleteEmployee = (
  options: UsePersonnelDeleteEmployeeOptions = {},
): UsePersonnelDeleteEmployeeResult => {
  const { onSuccess, onError, showSuccessToast = true, showErrorToast = true } = options;

  const queryClient = useQueryClient();
  const { t } = useTranslation('personnel-employees');
  const { handleError } = useApiErrorHandler();
  const { delete: deleteUseCase } = getEmployeeUseCases();

  const mutation: UseMutationResult<EmployeeDeleteResponseDto, Error, string> = useMutation({
    mutationFn: async (uuid: string) => {
      return deleteUseCase.execute({ userUuid: uuid });
    },
    onSuccess: (response) => {
      // Invalidate employees list to refresh the table
      queryClient.invalidateQueries(EMPLOYEES_QUERY_KEYS.list());
      // Remove deleted employee from detail cache
      queryClient.removeQueries(EMPLOYEES_QUERY_KEYS.detail(response.deletedUuid));
      // Invalidate roles list to refresh usersCount
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      if (showSuccessToast) {
        toast.success(t('notifications.deleteSuccess'), { duration: 3000 });
      }

      if (onSuccess) {
        onSuccess(response.deletedUuid);
      }
    },
    onError: (error: Error, uuid: string) => {
      if (showErrorToast) {
        handleError(error);
      }

      if (onError) {
        onError(error, uuid);
      }
    },
  });

  /**
   * Deletes an employee by UUID
   * Wraps mutation to maintain void return type
   */
  const deleteEmployee = useCallback(
    async (uuid: string): Promise<void> => {
      try {
        await mutation.mutateAsync(uuid);
      } catch {
        // Error is already handled by mutation's onError callback
      }
    },
    [mutation],
  );

  return {
    deleteEmployee,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
